/**
 * 업무 프로젝트(PMS) J3c1 — 업무 상세 서랍(화면 오른쪽).
 *
 * 업무 표에서 제목을 누르거나 주소 `?task={id}` 로 열린다. 열렸을 때만 마운트된다.
 * - 🔴 PATCH 는 '보낸 키' 만 바꾼다(서버 `model_fields_set`) → 바꾼 필드 하나만 보낸다.
 *   상태는 null 이 422 라 «없음» 선택지를 두지 않고, 유형·우선순위는 null 로 비운다.
 *   담당자는 여러 명(태그 묶음) — 바뀐 목록 통째를 `assignee_user_ids` 로 보낸다([] = 비움).
 * - 🔴 날짜는 change 마다 보내지 않는다 — 키보드로 연도를 치면 중간값(0002-…)마다 change 가 나서
 *   422 대화상자와 변경 알림이 여러 번 간다. blur·Enter 에서 '완전한 값' 일 때만 보낸다.
 *   값 일부만 지운 상태(`validity.badInput`)는 ''로 보이므로 '비움'으로 보내면 안 된다.
 * - 🔴 Enter 로 저장하면 실패 대화상자가 포커스를 가져가며 blur 가 한 번 더 난다 → 저장 중이거나
 *   마지막으로 보낸 값·서버 값과 같으면 보내지 않는다(같은 요청이 두 번 나가는 것을 막는다).
 * - 🔴 Esc 는 서랍 루트의 onKeyDown 에서만 받는다. window 리스너로 받으면 확인 대화상자의 Esc 가
 *   전파돼 서랍까지 닫힌다.
 * - 🔴 멤버에서 빠진 사람이 담당자·참조자로 남아 있을 수 있다(서버는 '이번에 새로 넣는 id' 만
 *   검증한다) → 그 사람을 선택지·체크 목록에 덧붙여 화면이 조용히 지우지 않게 한다.
 * - 🔴 다른 프로젝트의 업무를 `?task=` 로 열면 선택지·멤버·권한이 어긋난다 → «찾을 수 없음» 으로 막는다.
 * - 설명은 평문 편집이다(리치 편집기는 후속). 읽기는 서버가 정화한 HTML 을 그대로 그린다.
 * - 첨부는 TaskPmAttachments(설명 아래 구역)가 맡는다 — 탭이 아니다.
 * - 실패 문구는 지어내지 않고 서버 detail 을 그대로 보인다(errorText 로 문자열화).
 */
import { useEffect, useRef, useState } from 'react';
import { taskPmAPI } from '../../api/client';
import { errorText } from './errorText';
import { alertDialog, confirmDialog, toast } from '../../components/AppDialog';
import { copyToClipboard } from '../../utils/clipboard';
import TaskPmAttachments from './TaskPmAttachments';
import TaskPmComments from './TaskPmComments';
import TaskPmEvents from './TaskPmEvents';
import AssigneeTags, { assigneeIdsOf } from '../supportboard/AssigneeTags';
import { COLORS, badge, btn, card, input } from '../../utils/uiStyles';

const NOT_FOUND = '업무를 찾을 수 없거나 볼 권한이 없습니다.';
const SAVE_FAIL = '저장하지 못했습니다.';
const DELETE_CONFIRM = '이 업무를 지울까요? 되돌릴 수 없습니다.';
// 설명 HTML → 평문: 이 태그는 앞뒤를 줄바꿈으로 끊는다(그 밖의 요소는 자식만 이어 붙인다).
const BLOCK_TAGS = new Set(['P', 'DIV', 'LI', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const FULL_DATE = /^\d{4}-\d{2}-\d{2}$/;
const MIN_YEAR = 1900;
const DATE_FIELDS = ['start_date', 'due_date'];
// 서랍 맨 아래 활동 탭. 고른 탭만 마운트한다 → 안 고른 탭은 요청을 보내지 않는다.
// 🔴 첨부는 탭이 아니라 설명 아래 구역이다(L4 배치 정렬) — 여기 넣지 않는다.
const ACTIVITY_TABS = [
  { key: 'comments', label: '댓글' },
  { key: 'events', label: '기록' },
];

const statusOf = (e) => e?.response?.status;
const personName = (userId, name) => name || `사용자 #${userId}`;
const isFullDate = (value) => FULL_DATE.test(value) && Number(value.slice(0, 4)) >= MIN_YEAR;
const norm = (v) => (v == null ? '' : String(v));   // null·undefined → ''

// 설명 HTML 을 평문으로 푼다. 🔴 화면에 붙은 요소가 아니라 별도 문서에서 읽는다(스크립트 실행 없음).
export function htmlToText(html) {
  const doc = new DOMParser().parseFromString(html || '', 'text/html');
  let out = '';
  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) { out += child.nodeValue || ''; return; }   // 텍스트 노드
      if (child.nodeType !== 1) return;
      const tag = (child.tagName || '').toUpperCase();
      if (tag === 'BR') { out += '\n'; return; }
      if (BLOCK_TAGS.has(tag)) { out += '\n'; walk(child); out += '\n'; return; }
      walk(child);                                                         // ul·ol·strong 등
    });
  };
  walk(doc.body);
  return out.replace(/[ \t]*\n[ \t]*/g, '\n').replace(/\n{2,}/g, '\n').replace(/^\n+|\n+$/g, '');
}

const escapeHtml = (value) => String(value)
  .replace(/&/g, '&amp;')          // 🔴 & 를 먼저 바꾼다(나중에 하면 &lt; 가 다시 깨진다)
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;')
  .replace(/'/g, '&#39;');

// 평문 → 저장용 HTML: 줄마다 앞뒤 공백을 지우고 빈 줄은 건너뛴 뒤 <p> 로 감싼다.
export const textToHtml = (text) => String(text || '')
  .split('\n')
  .map((line) => line.trim())
  .filter(Boolean)
  .map((line) => `<p>${escapeHtml(line)}</p>`)
  .join('');

const headerRow = { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' };
// 구역은 카드 상자가 아니라 위아래로 이어지는 평평한 띠다(윗줄만 구분선).
const section = { padding: '16px 0', borderTop: `1px solid ${COLORS.borderSoft}` };
const sectionTitle = { margin: 0, fontSize: '16px' };
// «라벨 | 값» 한 줄 — 왼쪽 라벨 열은 고정 96px.
const fieldRow = { display: 'grid', gridTemplateColumns: '96px 1fr', alignItems: 'center', columnGap: '8px', minHeight: '44px' };
const fieldLabel = { fontSize: '12px', color: COLORS.textSub };
const itemRow = { display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 0', borderBottom: `1px solid ${COLORS.borderSoft}` };
const linkBtn = { background: 'none', border: 'none', padding: 0, color: COLORS.primaryDark, cursor: 'pointer', fontSize: '13px', textAlign: 'left' };

export default function TaskPmTaskDetail({ taskId, project, onOpenTask, onChanged, onClose, onDeleted }) {
  const [task, setTask] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState({ title: '', start_date: '', due_date: '' });
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleBusy, setTitleBusy] = useState(false);     // 제목 저장 중 — 입력칸을 읽기 전용으로 둔다
  const [descEdit, setDescEdit] = useState(null);        // null = 읽기, 문자열 = 편집 중
  const [descBusy, setDescBusy] = useState(false);
  const [textEdit, setTextEdit] = useState({ id: null, value: '' });
  const [watcherEdit, setWatcherEdit] = useState(null);  // null = 닫힘, 배열 = 고른 user_id
  const [newItem, setNewItem] = useState('');
  const [newSub, setNewSub] = useState('');
  // 🔴 활동 탭 상태는 조기 반환(아래 blocked·!task)보다 앞인 이 선언부에 둔다 — 렌더 중간에
  //    훅을 선언하면 «Rendered more hooks than during the previous render» 가 난다.
  const [activeTab, setActiveTab] = useState('comments');
  const [activityKey, setActivityKey] = useState(0);   // 바뀌면 «기록» 탭이 다시 읽는다

  const rootRef = useRef(null);
  const sendingRef = useRef({});   // 필드별 '저장 중'
  const sentRef = useRef({});      // 필드별 '마지막으로 보낸 값'
  // 입력칸에 마지막으로 맞춘 서버 값(null = 아직 한 번도 안 맞춤) — 아래 동기화 effect 가 쓴다.
  const syncedRef = useRef({ title: null, start_date: null, due_date: null });
  // 날짜 전용(아래 동기화 effect·commitValue 가 쓴다).
  const dirtyRef = useRef({});     // 필드별: 마지막 맞춤·전송 완료 뒤 사용자가 그 칸을 바꿨다
  const pendingRef = useRef({});   // 필드별: 저장 중에 blur 한 «그때 검증한 값»(문자열)
  const serverRef = useRef({ start_date: '', due_date: '' });   // 정규화한 최신 서버 날짜
  const draftRef = useRef({});     // 필드별: 입력칸 날짜의 최신 값 — setDraft 와 같은 자리에서 동기로 갱신
  // 제목 편집 여부는 ref 로도 들고 있는다 — 아래 동기화 effect 가 '지금' 값을 봐야 하기 때문(의존성 아님).
  const titleEditRef = useRef(false);
  const setTitleEditing = (on) => { titleEditRef.current = on; setEditingTitle(on); };
  // 응답 순번 — 서버 시각(updated_at)은 단조가 아니라 «보낸 순서 번호»로 늦게 온 옛 응답을 가린다.
  const seqRef = useRef(0);          // 상세를 바꾸거나 다시 읽는 요청을 보낼 때마다 1씩 — 보낸 순서
  const appliedSeqRef = useRef(0);   // 화면에 넣은 상세 중 가장 늦게 보낸 요청의 번호
  const resyncRef = useRef({ running: false, again: false });
  const staleAfterRef = useRef(null);   // 응답을 버린 순간의 seqRef(null = 낡지 않음)
  const aliveRef = useRef(true);
  const nextSeq = () => { seqRef.current += 1; return seqRef.current; };

  // ── 불러오기
  useEffect(() => {
    let alive = true;
    setTask(null);
    setLoadError('');
    taskPmAPI.getTask(taskId)
      .then((res) => { if (alive) setTask(res?.data || null); })
      .catch((e) => {
        if (!alive) return;
        const code = statusOf(e);
        setTask(null);
        setLoadError(code === 404 || code === 403 || code === 422 ? NOT_FOUND : errorText(e, NOT_FOUND));
      });
    return () => { alive = false; };
  }, [taskId]);

  // 서랍이 열리면 포커스를 가져온다(Esc 를 루트에서 받으려면 포커스가 안에 있어야 한다).
  useEffect(() => { rootRef.current?.focus(); }, []);

  // 닫힌 뒤에는 다시 읽기를 보내지 않는다(resync 가 본다).
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // 서버 값이 '바뀐 것' 만 입력칸에 다시 맞춘다(저장 성공·다른 변경 공통).
  // 🔴 상세는 체크리스트·참조자 변경에도 통째로 새로 온다 — 그때마다 덮으면 그 찰나에 고르던
  //    날짜가 지워진다. 그래서 직전에 맞춘 서버 값과 견줘 달라졌을 때만 덮는다.
  // 제목은 편집 중이면 건드리지 않는다 — 실패 복원은 저장 쪽에서 따로 한다.
  // 날짜는 값 비교가 아니라 "편집함" 표시로 판정한다 — 저장 응답이 오기 전에 다시 고른 값(되돌린 값 포함)을 지우지 않는다. 표시는 그 값의 전송이 끝났을 때·보낼 게 없을 때·되돌릴 때만 끈다.
  // 저장 중 blur 한 값은 앞 저장이 끝난 뒤 그 값이 여전히 입력칸에 있고 방금 보낸 값과 다를 때만 한 번 더 보낸다(최신 서버 값과 같으면 보내지 않고 표시만 끈다).
  // 날짜 비교의 서버 값은 serverRef(applyDetail 이 setTask 앞에서 갱신)에서 읽는다 — 클로저 task 는 재전송 시점에 낡았다.
  useEffect(() => {
    if (!task) {
      syncedRef.current = { title: null, start_date: null, due_date: null };
      dirtyRef.current = {};
      pendingRef.current = {};
      draftRef.current = {};
      serverRef.current = { start_date: '', due_date: '' };
      return;
    }
    const server = {
      title: task.title || '',
      start_date: task.start_date || '',
      due_date: task.due_date || '',
    };
    const prevServer = syncedRef.current;
    syncedRef.current = server;
    // 날짜는 여기서 먼저 정하고 setDraft 는 그 결정만 반영한다(undefined = 입력값을 둔다).
    const nextDate = {};
    DATE_FIELDS.forEach((field) => {
      if (prevServer[field] === null) {                 // 첫 맞춤 — 서버 값
        dirtyRef.current[field] = false;
        draftRef.current[field] = server[field];
        serverRef.current[field] = server[field];      // 첫 불러오기는 applyDetail 을 거치지 않는다
        nextDate[field] = server[field];
      } else if (dirtyRef.current[field] || server[field] === prevServer[field]) {
        nextDate[field] = undefined;                    // 편집함·서버 값 그대로 — 입력값을 둔다
      } else {                                          // 서버 값이 바뀌었다 — 서버 값
        draftRef.current[field] = server[field];
        nextDate[field] = server[field];
      }
    });
    setDraft((prev) => ({
      title: titleEditRef.current ? prev.title : server.title,
      start_date: nextDate.start_date === undefined ? prev.start_date : nextDate.start_date,
      due_date: nextDate.due_date === undefined ? prev.due_date : nextDate.due_date,
    }));
  }, [task]);

  const reportError = (e, fallback = SAVE_FAIL) => {
    const message = errorText(e, fallback);
    if (statusOf(e)) alertDialog(message); else toast(message, 'error');
  };

  // 상세 한 벌을 화면에 넣는다 — 더 늦게 보낸 요청의 응답이 이미 들어왔으면 넣지 않고 false.
  // 버린 «뒤에» 번호를 받은 요청(다시 읽기 포함)의 응답만 낡음 표시를 푼다.
  const applySnapshot = (data, seq) => {
    if (!data || typeof data !== 'object') return false;
    if (seq < appliedSeqRef.current) return false;
    appliedSeqRef.current = seq;
    if (staleAfterRef.current !== null && seq > staleAfterRef.current) staleAfterRef.current = null;
    serverRef.current = { start_date: norm(data.start_date), due_date: norm(data.due_date) };
    setTask(data);
    return true;
  };

  // 늦게 온 옛 응답을 버렸다 = 화면이 서버와 어긋났을 수 있다 → 상세를 한 번 다시 읽는다.
  // 다시 읽는 중에 또 버리면 끝난 뒤 한 번 더(겹쳐 보내지 않는다). 실패는 조용히 넘긴다.
  const resync = () => {
    if (!aliveRef.current) return;
    const r = resyncRef.current;
    if (r.running) { r.again = true; return; }
    r.running = true;
    const seq = nextSeq();
    taskPmAPI.getTask(taskId)
      .then((res) => { applySnapshot(res?.data, seq); })
      .catch(() => {})
      .finally(() => {
        r.running = false;
        if (!r.again || !aliveRef.current) return;
        r.again = false;
        resync();
      });
  };

  // 🔴 성공한 변경은 모두 여기로 — 업무 표(체크리스트 n/m·상태 등)가 낡지 않게 알린다.
  // seq 는 그 요청을 «보내기 직전»에 nextSeq() 로 받은 번호.
  const applyDetail = (data, seq) => {
    if (data && typeof data === 'object' && !applySnapshot(data, seq)) {
      staleAfterRef.current = seqRef.current;   // 버린 응답이 실제 최신일 수 있다 — 같은 값 생략을 끈다
      resync();
    }
    setActivityKey((k) => k + 1);   // 필드가 바뀌었으니 «기록» 탭의 목록도 낡았다
    onChanged();
  };

  // 날짜는 serverRef 에서 읽는다(재전송이 낡은 클로저를 타도 최신 서버 값을 본다). 제목은 task.
  const serverValue = (field) => {
    if (DATE_FIELDS.includes(field)) return serverRef.current[field];
    const value = task ? task[field] : null;
    return value === null || value === undefined ? '' : String(value);
  };

  const patchField = async (field, value, fallback) => {
    if (!task || sendingRef.current[field]) return false;
    sendingRef.current[field] = true;
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.updateTask(task.id, { [field]: value });
      applyDetail(res?.data, seq);
      return true;
    } catch (e) {
      reportError(e, fallback);
      return false;
    } finally {
      sendingRef.current[field] = false;
    }
  };

  // 🔴 텍스트·날짜 공통 규칙 — 저장 중이거나 '마지막으로 보낸 값·서버 값' 과 같으면 보내지 않는다.
  //    단 옛 응답을 버린 뒤(staleAfterRef)에는 서버 값이 낡았을 수 있어 같아 보여도 보낸다.
  const commitValue = async (field, value, fallback) => {
    if (!task || sendingRef.current[field] || sentRef.current[field] === value) return false;
    const isDate = DATE_FIELDS.includes(field);
    const server = serverValue(field);
    if (value === server && staleAfterRef.current === null) {
      if (isDate) dirtyRef.current[field] = false;   // 보낼 게 없다 — 편집함 표시를 끈다
      return false;
    }
    sentRef.current[field] = value;
    let ok;
    try {
      ok = await patchField(field, value === '' ? null : value, fallback);
      if (!isDate) {
        if (!ok) setDraft((prev) => ({ ...prev, [field]: server }));   // 실패하면 서버 값으로 되돌린다
      } else if (norm(draftRef.current[field]) === norm(value)) {
        // 날짜: 입력이 보낸 값 그대로일 때만 마무리한다 — 응답 전에 또 바꿨으면 입력·표시를 둔다.
        if (!ok) {                                   // 실패하면 서버 값으로 되돌린다
          const back = serverValue(field);
          draftRef.current[field] = back;
          setDraft((prev) => ({ ...prev, [field]: back }));
        }
        dirtyRef.current[field] = false;
      }
    } finally {
      delete sentRef.current[field];     // 같은 값으로 다시 시도할 수 있게 푼다
    }
    // 저장 중 blur 로 남겨 둔 날짜 — 그 값이 아직 입력칸에 있고 방금 보낸 값과 다를 때만 한 번 더 보낸다.
    if (isDate) {
      const pv = pendingRef.current[field];
      delete pendingRef.current[field];
      if (pv !== undefined
          && norm(draftRef.current[field]) === pv     // blur 뒤 다시 손대지 않았다
          && pv !== norm(value)) {                    // 방금 보낸 값과 다르다
        await commitValue(field, pv, fallback);       // '' 도 그대로 넘긴다(위에서 null 로 바꾼다)
      }
    }
    return ok;
  };

  const commitTitle = async () => {
    if (!task || sendingRef.current.title) return;      // 저장 중이면 편집 상태도 그대로 둔다
    const next = draft.title.trim();
    if (!next || next === (task.title || '')) {         // 공백·그대로면 되돌리고 편집을 끝낸다
      setDraft((prev) => ({ ...prev, title: task.title || '' }));
      setTitleEditing(false);
      return;
    }
    // 저장 중에는 입력칸을 읽기 전용으로 둔다 — 그사이 친 글자를 응답(닫기·되돌리기)이 지우지 않게.
    setTitleBusy(true);
    let ok = false;
    try {
      ok = await commitValue('title', next, '제목을 저장하지 못했습니다.');
    } finally {
      setTitleBusy(false);
    }
    if (ok) setTitleEditing(false);
  };

  const commitDate = (field, e) => {
    if (!task) return;
    const value = draft[field];
    // 🔴 badInput 은 '날짜 칸을 일부만 채운' 상태다 — 그때만 값이 '' 로 읽히므로 '비움' 과 구별하려고 본다.
    //    값이 온전하면 보지 않는다: 브라우저는 온전한 값에 badInput 을 켜지 않고, 일부 환경(jsdom)은
    //    date 칸에 늘 켜 두기도 해서 — 그걸 그대로 믿으면 멀쩡한 날짜 저장이 통째로 막힌다.
    const bad = value === '' && Boolean(e?.target?.validity?.badInput);
    // 일부만 채운 날짜·불완전한 연도는 저장하지 않고 원래 값으로 되돌린다.
    if (bad || (value !== '' && !isFullDate(value))) {
      const back = serverValue(field);
      dirtyRef.current[field] = false;
      draftRef.current[field] = back;
      setDraft((prev) => ({ ...prev, [field]: back }));
      return;
    }
    // 저장 중이면 보내지 않고 그 값을 남겨 둔다 — 앞 저장이 끝난 뒤 commitValue 가 이어 보낸다.
    if (sendingRef.current[field]) {
      pendingRef.current[field] = norm(value);
      return;
    }
    commitValue(field, value, '날짜를 저장하지 못했습니다.');
  };

  const enterKey = (e, run) => {
    // 한글 입력 중의 Enter(조합 확정)는 저장이 아니다.
    if (e.key !== 'Enter' || e.nativeEvent?.isComposing) return;
    e.preventDefault();
    run();
  };

  const saveDescription = async () => {
    if (!task || descBusy) return;
    const html = textToHtml(descEdit);
    setDescBusy(true);
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.updateTask(task.id, { description_html: html });
      applyDetail(res?.data, seq);
      setDescEdit(null);
    } catch (e) {
      reportError(e, '설명을 저장하지 못했습니다.');
    } finally {
      setDescBusy(false);
    }
  };

  // ── 체크리스트
  const patchItem = async (itemId, body, fallback) => {
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.updateChecklist(itemId, body);
      applyDetail(res?.data, seq);
    } catch (e) {
      reportError(e, fallback);
    }
  };

  const commitItemText = (item) => {
    if (textEdit.id !== item.id) return;               // 이미 끝난 편집(Enter 뒤 blur)은 흘린다
    const next = textEdit.value.trim();
    setTextEdit({ id: null, value: '' });
    if (!next || next === item.text) return;
    patchItem(item.id, { text: next }, '항목 문구를 바꾸지 못했습니다.');
  };

  const addItem = async () => {
    const text = newItem.trim();
    if (!task || !text) return;
    setNewItem('');
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.addChecklist(task.id, text);
      applyDetail(res?.data, seq);
    } catch (e) {
      setNewItem(text);                                // 실패하면 입력을 돌려준다
      reportError(e, '항목을 추가하지 못했습니다.');
    }
  };

  // ── 하위 업무 만들기 — 서버가 «하위 아래 하위»·보관된 부모를 422 로 막는다(문구는 그대로 보인다).
  // 응답은 새 업무라 이 상세가 아니다 → 상세를 다시 읽어 하위 목록·개수를 맞춘다.
  // 🔴 만들기는 성공했는데 다시 읽기만 실패하면 입력을 되돌리지 않는다 — 되돌리면 실패로 보여
  //    다시 눌러 같은 하위 업무가 하나 더 생긴다. 그때도 표는 onChanged 로 다시 읽게 한다.
  const addSubtask = async () => {
    const title = newSub.trim();
    if (!task || !title) return;
    setNewSub('');
    try {
      await taskPmAPI.createTask(project.id, { title, parent_task_id: task.id });
    } catch (e) {
      setNewSub(title);
      reportError(e, '하위 업무를 만들지 못했습니다.');
      return;
    }
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.getTask(task.id);
      applyDetail(res?.data, seq);
    } catch {
      applyDetail(null);                               // 상세는 그대로 두고 표·기록만 갱신
      toast('하위 업무는 만들었지만 목록을 다시 읽지 못했습니다.', 'error');
    }
  };

  const moveItem = async (index, delta) => {
    const items = task?.checklist || [];
    const target = index + delta;
    if (target < 0 || target >= items.length) return;
    const ids = items.map((item) => item.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      const seq = nextSeq();
      const res = await taskPmAPI.reorderChecklist(task.id, ids);
      applyDetail(res?.data, seq);
    } catch (e) {
      reportError(e, '항목 순서를 바꾸지 못했습니다.');
    }
  };

  const removeItem = async (item) => {
    try {
      await taskPmAPI.deleteChecklist(item.id);        // 204 — 상세가 안 오니 다시 읽는다
      const seq = nextSeq();
      const res = await taskPmAPI.getTask(task.id);
      applyDetail(res?.data, seq);
    } catch (e) {
      reportError(e, '항목을 지우지 못했습니다.');
    }
  };

  // ── 보관·삭제
  const toggleArchive = async () => {
    try {
      const seq = nextSeq();
      const res = task.archived_at
        ? await taskPmAPI.unarchiveTask(task.id)
        : await taskPmAPI.archiveTask(task.id);
      applyDetail(res?.data, seq);
    } catch (e) {
      reportError(e, '보관 상태를 바꾸지 못했습니다.');
    }
  };

  const removeTask = async () => {
    if (!(await confirmDialog(DELETE_CONFIRM))) return;
    try {
      await taskPmAPI.deleteTask(task.id);
      onDeleted();
    } catch (e) {
      reportError(e, '업무를 지우지 못했습니다.');
    }
  };

  // 키 배지 = 이 업무로 바로 오는 주소. 눌러서 복사하고 결과를 알린다
  // (클립보드 권한이 없는 환경도 있으므로 성공·실패 둘 다 알린다).
  const copyLink = () => {
    if (!task) return;
    copyToClipboard(`${window.location.origin}/tasks/${project.id}?task=${task.id}`).then(
      () => toast('링크를 복사했습니다.'),
      () => toast('링크를 복사하지 못했습니다.', 'error'),
    );
  };

  const handleKeyDown = (e) => {
    if (e.key !== 'Escape' || e.defaultPrevented) return;
    if (editingTitle) {
      setDraft((prev) => ({ ...prev, title: task?.title || '' }));
      setTitleEditing(false);
      return;
    }
    if (descEdit !== null) { if (!descBusy) setDescEdit(null); return; }   // 저장 중이면 닫지 않는다
    if (textEdit.id !== null) { setTextEdit({ id: null, value: '' }); return; }
    onClose();
  };

  const shell = (children) => (
    <div ref={rootRef} tabIndex={-1} role="dialog" aria-label="업무 상세" onKeyDown={handleKeyDown}
      style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(480px, 100vw)', zIndex: 60,
        background: COLORS.surface, borderLeft: `1px solid ${COLORS.border}`,
        boxShadow: '-2px 0 12px rgba(15,23,42,.12)', overflowY: 'auto', padding: '14px 20px 20px', outline: 'none',
      }}>
      {children}
    </div>
  );

  const closeButton = (
    <button type="button" onClick={onClose} style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>닫기</button>
  );

  // 🔴 다른 프로젝트의 업무는 못 본 것으로 한다 — 선택지·멤버가 이 프로젝트 기준이라 어긋난다.
  const mismatched = Boolean(task) && String(task.project_id) !== String(project.id);
  const blocked = loadError || (mismatched ? NOT_FOUND : '');

  if (blocked) {
    return shell(
      <>
        <div style={headerRow}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>업무 상세</h3>
          {closeButton}
        </div>
        <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText }}>{blocked}</div>
      </>
    );
  }

  if (!task) {
    return shell(
      <>
        <div style={headerRow}>
          <h3 style={{ margin: 0, fontSize: '16px' }}>업무 상세</h3>
          {closeButton}
        </div>
        <p role="status" style={{ fontSize: '12px', color: COLORS.textSub }}>불러오는 중…</p>
      </>
    );
  }

  // ── 권한(화면 판단). 서버가 403 이면 그 문구를 그대로 보인다.
  const isEditor = project.my_role === 'manager' || project.my_role === 'member';
  const canArchive = isEditor && !project.closed;        // 보관·보관 해제
  const canWrite = canArchive && !task.archived_at;      // 필드·설명·참조자·체크리스트·삭제
  const notice = !isEditor
    ? '읽기 권한입니다. 업무를 바꿀 수 없습니다.'
    : (project.closed ? '종료된 프로젝트입니다. 다시 열어야 업무를 바꿀 수 있습니다.' : '');

  const groups = project.options || {};
  const statuses = groups.status || [];
  const types = groups.type || [];
  const priorities = groups.priority || [];
  const members = project.members || [];
  const items = task.checklist || [];
  const doneCount = items.filter((item) => item.done).length;

  // 멤버에서 빠진 현재 담당자·참조자도 선택지에 남긴다(화면이 조용히 지우면 안 된다).
  // 담당자 목록이 정본이고, 목록이 없는 옛 응답은 assignee_user_id 하나다(assigneeIdsOf).
  // 이름은 assignee_names 의 같은 자리 — 옛 응답(한 명)이면 assignee_name.
  // 후보(people)는 멤버 + '서버가 준' 현재 담당자뿐이다 → 멤버에서 빠진 사람을 칩에서 빼 저장되면 후보에서도 사라진다.
  const assigneeIds = assigneeIdsOf(task);
  const assigneePeople = members.map((m) => ({ id: m.user_id, name: m.name, email: m.email }));
  assigneeIds.forEach((id, index) => {
    if (assigneePeople.some((p) => p.id === id)) return;
    const listed = Array.isArray(task.assignee_names) ? task.assignee_names[index]
      : (assigneeIds.length === 1 ? task.assignee_name : null);
    assigneePeople.push({ id, name: personName(id, listed) });
  });
  const watcherCandidates = members.map((m) => ({ id: m.user_id, name: m.name || m.email }));
  (task.watchers || []).forEach((w) => {
    if (!watcherCandidates.some((c) => c.id === w.user_id)) {
      watcherCandidates.push({ id: w.user_id, name: personName(w.user_id, w.name) });
    }
  });

  // 🔴 라벨 글자는 왼쪽 열(fieldLine)이 맡는다 — 컨트롤은 aria-label 만 달고 값 칸을 채운다
  //    (여기서 글자를 또 그리면 라벨이 두 번 보인다).
  const optionSelect = (label, field, options, allowNone) => (
    <select aria-label={label} disabled={!canWrite}
      value={task[field] === null || task[field] === undefined ? '' : String(task[field])}
      onChange={(e) => patchField(field, e.target.value === '' ? null : Number(e.target.value), `${label}을(를) 바꾸지 못했습니다.`)}
      style={{ ...input, width: '100%' }}>
      {allowNone && <option value="">없음</option>}
      {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
    </select>
  );

  const dateInput = (label, field) => (
    <input type="date" aria-label={label} disabled={!canWrite} value={draft[field]}
      onChange={(e) => {
        const v = e.target.value;
        dirtyRef.current[field] = true;
        draftRef.current[field] = v;
        setDraft((prev) => ({ ...prev, [field]: v }));
      }}
      onBlur={(e) => commitDate(field, e)}
      onKeyDown={(e) => enterKey(e, () => commitDate(field, e))}
      style={{ ...input, width: '140px' }} />
  );

  const fieldLine = (label, cell) => (
    <div style={fieldRow}>
      <span style={fieldLabel}>{label}</span>
      {cell}
    </div>
  );

  return shell(
    <>
      <div style={headerRow}>
        {/* 키 배지를 누르면 이 업무 주소가 복사된다(글자는 키 그대로). */}
        <button type="button" aria-label={`${task.key} 링크 복사`} onClick={copyLink}
          style={{ ...badge('default'), border: 'none', cursor: 'pointer', fontFamily: 'inherit' }}>{task.key}</button>
        {task.archived_at && <span style={badge('warn')}>보관된 업무입니다</span>}
        {closeButton}
      </div>

      {editingTitle
        ? (
          <input aria-label="제목" value={draft.title} autoFocus
            readOnly={titleBusy} aria-busy={titleBusy}
            onChange={(e) => { const v = e.target.value; setDraft((prev) => ({ ...prev, title: v })); }}
            onBlur={commitTitle}
            onKeyDown={(e) => enterKey(e, commitTitle)}
            style={{ ...input, fontSize: '20px', marginBottom: '10px' }} />
        )
        : (
          <button type="button" aria-label="제목 편집" disabled={!canWrite}
            onClick={() => { setDraft((prev) => ({ ...prev, title: task.title || '' })); setTitleEditing(true); }}
            style={{ ...linkBtn, color: COLORS.text, fontSize: '22px', fontWeight: 600, marginBottom: '10px', display: 'block', width: '100%' }}>
            {task.title}
          </button>
        )}

      {notice && <p style={{ margin: '0 0 10px', fontSize: '12px', color: COLORS.textSub }}>{notice}</p>}

      {/* 필드는 «라벨 | 값» 한 줄씩 — 담당자·참조·기간·상태·유형·우선순위·(상위 업무) 순서. */}
      <section aria-label="업무 정보" style={section}>
        {/* 칩은 서버 값(task)에서 그린다 — 저장이 실패하면 그대로 원래 목록이 남는다. */}
        {fieldLine('담당자', (
          <AssigneeTags ids={assigneeIds} people={assigneePeople} groupLabel="담당자" disabled={!canWrite}
            onChange={(ids) => patchField('assignee_user_ids', ids, '담당자를 바꾸지 못했습니다.')} />
        ))}
        {fieldLine('참조', (
          <div role="region" aria-label="참조자">
            {watcherEdit === null
              ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
                  {(task.watchers || []).map((w) => (
                    <span key={w.user_id} style={badge('default')}>{personName(w.user_id, w.name)}</span>
                  ))}
                  {(task.watchers || []).length === 0 && <span style={{ fontSize: '12px', color: COLORS.textSub }}>없음</span>}
                  <button type="button" aria-label="참조자 편집" disabled={!canWrite}
                    onClick={() => setWatcherEdit((task.watchers || []).map((w) => w.user_id))}
                    style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>참조자 편집</button>
                </div>
              )
              : (
                <div style={{ padding: '4px 0' }}>
                  <ul style={{ listStyle: 'none', margin: '0 0 6px', padding: 0 }}>
                    {watcherCandidates.map((c) => (
                      <li key={c.id} style={{ fontSize: '13px', padding: '3px 0' }}>
                        <label>
                          <input type="checkbox" aria-label={`${c.name} 참조`} checked={watcherEdit.includes(c.id)}
                            onChange={(e) => setWatcherEdit(e.target.checked
                              ? [...watcherEdit, c.id]
                              : watcherEdit.filter((id) => id !== c.id))} />
                          {c.name}
                        </label>
                      </li>
                    ))}
                  </ul>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button type="button" aria-label="참조자 취소" onClick={() => setWatcherEdit(null)} style={btn('secondary', 'sm')}>취소</button>
                    <button type="button" aria-label="참조자 적용" style={btn('primary', 'sm')}
                      onClick={async () => {
                        const ids = watcherCandidates.filter((c) => watcherEdit.includes(c.id)).map((c) => c.id);
                        try {
                          const seq = nextSeq();
                          const res = await taskPmAPI.setWatchers(task.id, ids);
                          applyDetail(res?.data, seq);
                          setWatcherEdit(null);
                        } catch (e) {
                          reportError(e, '참조자를 저장하지 못했습니다.');
                        }
                      }}>적용</button>
                  </div>
                </div>
              )}
          </div>
        ))}
        {fieldLine('기간', (
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px' }}>
            {dateInput('시작일', 'start_date')}
            <span style={{ fontSize: '12px', color: COLORS.textSub }}>—</span>
            {dateInput('마감일', 'due_date')}
          </div>
        ))}
        {fieldLine('상태', optionSelect('상태', 'status_id', statuses, false))}
        {fieldLine('유형', optionSelect('유형', 'type_id', types, true))}
        {fieldLine('우선순위', optionSelect('우선순위', 'priority_id', priorities, true))}
        {task.parent_task_id && fieldLine('상위 업무', (
          <button type="button" aria-label="상위 업무 열기" onClick={() => onOpenTask(task.parent_task_id)} style={linkBtn}>
            {`상위: ${task.parent_key || ''} ${task.parent_title || ''}`}
          </button>
        ))}
      </section>

      {/* 🔴 구역 이름을 '설명' 으로 두면 편집 textarea(aria-label="설명")와 같은 이름이 둘이 된다. */}
      <section aria-label="업무 설명" style={section}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h4 style={sectionTitle}>설명</h4>
          {descEdit === null
            ? (
              <button type="button" aria-label="설명 편집" disabled={!canWrite}
                onClick={() => setDescEdit(htmlToText(task.description_html))}
                style={{ ...btn('secondary', 'sm'), marginLeft: 'auto' }}>설명 편집</button>
            )
            : (
              <span style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                <button type="button" aria-label="설명 취소" onClick={() => setDescEdit(null)} disabled={descBusy}
                  style={btn('secondary', 'sm')}>취소</button>
                <button type="button" aria-label="설명 저장" onClick={saveDescription} disabled={descBusy}
                  style={{ ...btn('primary', 'sm'), opacity: descBusy ? 0.5 : 1 }}>저장</button>
              </span>
            )}
        </div>
        {descEdit === null
          ? (
            <div style={{ fontSize: '13px', color: COLORS.text, wordBreak: 'break-word' }}
              dangerouslySetInnerHTML={{ __html: task.description_html || '' }} />
          )
          : (
            <textarea aria-label="설명" rows={6} value={descEdit}
              readOnly={descBusy} aria-busy={descBusy}
              onChange={(e) => setDescEdit(e.target.value)}
              style={{ ...input, resize: 'vertical' }} />
          )}
      </section>

      {/* 첨부는 탭이 아니라 설명 다음 구역이다(잔디 배치). 목록·올리기·내려받기·지우기는 TaskPmAttachments. */}
      <section aria-label="첨부파일" style={section}>
        <TaskPmAttachments taskId={task.id} project={project} canWrite={canWrite} onChanged={() => applyDetail(null)} />
      </section>

      <section aria-label="체크리스트" style={section}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <h4 style={sectionTitle}>체크리스트</h4>
          {/* 🔴 «n/m 완료» 와 «%» 는 따로 둔다 — 한 덩어리로 붙이면 시험의 getByText('1/2 완료') 가 깨진다. */}
          <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`${doneCount}/${items.length} 완료`}</span>
          {items.length > 0 && (
            <span style={{ fontSize: '12px', color: COLORS.textSub }}>{`${Math.round((doneCount * 100) / items.length)}%`}</span>
          )}
        </div>
        <ul style={{ listStyle: 'none', margin: '0 0 10px', padding: 0 }}>
          {items.map((item, index) => (
            <li key={item.id} style={itemRow}>
              <input type="checkbox" aria-label={`${item.text} 완료`} checked={Boolean(item.done)} disabled={!canWrite}
                onChange={(e) => patchItem(item.id, { done: e.target.checked }, '완료 여부를 바꾸지 못했습니다.')} />
              <input aria-label={`${item.text} 문구`} disabled={!canWrite}
                value={textEdit.id === item.id ? textEdit.value : item.text}
                onChange={(e) => setTextEdit({ id: item.id, value: e.target.value })}
                onBlur={() => commitItemText(item)}
                onKeyDown={(e) => enterKey(e, () => commitItemText(item))}
                style={{ ...input, flex: 1, minWidth: '80px' }} />
              <button type="button" aria-label={`${item.text} 위로`} disabled={!canWrite || index === 0}
                onClick={() => moveItem(index, -1)} style={btn('secondary', 'sm')}>위로</button>
              <button type="button" aria-label={`${item.text} 아래로`} disabled={!canWrite || index === items.length - 1}
                onClick={() => moveItem(index, 1)} style={btn('secondary', 'sm')}>아래로</button>
              <button type="button" aria-label={`${item.text} 삭제`} disabled={!canWrite}
                onClick={() => removeItem(item)} style={btn('danger', 'sm')}>삭제</button>
            </li>
          ))}
          {items.length === 0 && <li style={{ fontSize: '12px', color: COLORS.textSub }}>항목이 없습니다.</li>}
        </ul>
        <div style={{ display: 'flex', gap: '6px' }}>
          <input aria-label="새 체크리스트 항목" placeholder="항목 내용" value={newItem} disabled={!canWrite}
            onChange={(e) => setNewItem(e.target.value)}
            onKeyDown={(e) => enterKey(e, addItem)}
            style={{ ...input, flex: 1, minWidth: '120px' }} />
          {/* 🔴 이름은 «항목 추가» — 업무 화면의 «추가» 와 겹치면 안 된다. */}
          <button type="button" onClick={addItem} disabled={!canWrite || !newItem.trim()}
            style={{ ...btn('primary', 'sm'), opacity: canWrite && newItem.trim() ? 1 : 0.5 }}>항목 추가</button>
        </div>
      </section>

      <section aria-label="하위 업무" style={section}>
        <h4 style={{ ...sectionTitle, marginBottom: '6px' }}>{`하위 업무 (${(task.subtasks || []).length})`}</h4>
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {(task.subtasks || []).map((sub) => (
            <li key={sub.id} style={{ padding: '4px 0' }}>
              {/* 키와 제목 사이에 공백 문자가 없다 → 이름은 aria-label 로 또렷하게 준다. */}
              <button type="button" aria-label={`${sub.key} ${sub.title}`} onClick={() => onOpenTask(sub.id)} style={linkBtn}>
                <span style={{ color: COLORS.textSub, marginRight: '6px' }}>{sub.key}</span>
                <span style={{ textDecoration: sub.is_done ? 'line-through' : 'none' }}>{sub.title}</span>
              </button>
            </li>
          ))}
          {(task.subtasks || []).length === 0 && <li style={{ fontSize: '12px', color: COLORS.textSub }}>하위 업무가 없습니다.</li>}
        </ul>
        {/* 🔴 하위 업무에는 다시 하위를 둘 수 없다(서버도 422) → 만드는 줄 자체를 그리지 않는다. */}
        {canWrite && !task.parent_task_id && (
          <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
            <input aria-label="새 하위 업무 제목" placeholder="하위 업무 제목" value={newSub}
              onChange={(e) => setNewSub(e.target.value)}
              onKeyDown={(e) => enterKey(e, addSubtask)}
              style={{ ...input, flex: 1, minWidth: '120px' }} />
            {/* 🔴 이름은 «하위 업무 추가» — «추가»·«항목 추가» 와 겹치면 안 된다. */}
            <button type="button" onClick={addSubtask} disabled={!newSub.trim()}
              style={{ ...btn('primary', 'sm'), opacity: newSub.trim() ? 1 : 0.5 }}>하위 업무 추가</button>
          </div>
        )}
      </section>

      <section aria-label="활동" style={section}>
        <div role="tablist" aria-label="활동 탭"
          style={{ display: 'flex', borderBottom: `1px solid ${COLORS.borderSoft}`, marginBottom: '10px' }}>
          {ACTIVITY_TABS.map((tab) => {
            const on = activeTab === tab.key;
            return (
              <button key={tab.key} type="button" role="tab" aria-selected={on}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  background: 'none', border: 'none', borderBottom: `2px solid ${on ? COLORS.primary : 'transparent'}`,
                  padding: '8px 4px', marginRight: '12px', fontSize: '13px', fontWeight: on ? 700 : 500,
                  color: on ? COLORS.text : COLORS.textSub, cursor: 'pointer', fontFamily: 'inherit',
                }}>{tab.label}</button>
            );
          })}
        </div>
        {activeTab === 'comments' && <TaskPmComments taskId={task.id} project={project} canWrite={canWrite} />}
        {activeTab === 'events' && <TaskPmEvents taskId={task.id} reloadKey={activityKey} />}
      </section>

      <div style={{ display: 'flex', gap: '8px' }}>
        <button type="button" onClick={toggleArchive} disabled={!canArchive}
          style={{ ...btn('secondary', 'sm'), opacity: canArchive ? 1 : 0.5 }}>
          {task.archived_at ? '보관 해제' : '보관'}
        </button>
        <button type="button" onClick={removeTask} disabled={!canWrite}
          style={{ ...btn('danger', 'sm'), opacity: canWrite ? 1 : 0.5 }}>삭제</button>
      </div>
    </>
  );
}
